import React from 'react';
import { Text, useTheme } from '@fluentui/react';
import {
    CloudData,
    HealthStatus,
    StageType,
} from '../../models/CloudStatusModels';
import { Ellipsis } from '../../../HubLayout/components/Utilities/Ellipsis';
import {
    getStatusColor,
    getStatusIcon,
    isSmallMobileWidth,
    isMobileWidth,
} from '../../utils/StatusHelpers';
import '../../styles/CommonStyles.css';

interface CurrentTabProps {
    cloudData: CloudData;
    selectedStage: StageType;
    onStageClick: (stage: StageType) => void;
}

export const CurrentTab: React.FC<CurrentTabProps> = ({
    cloudData,
    selectedStage,
    onStageClick,
}) => {
    const theme = useTheme();
    const statusColor = getStatusColor(cloudData.overallHealth);

    // Calculate health for each stage
    const getStageHealth = (
        stageName:
            | 'public'
            | 'publicTransfer'
            | 'cloudArrive'
            | 'cloudEngine'
            | 'cloudPlatform'
    ): HealthStatus => {
        // Check if pipelines exist and have valid data
        if (!cloudData.pipelines || cloudData.pipelines.length === 0) {
            return 'Unknown';
        }

        // Ensure all pipelines have the stages structure
        const hasUnhealthy = cloudData.pipelines.some(
            (p) =>
                p.stages &&
                p.stages[stageName] &&
                p.stages[stageName].status === 'Unhealthy'
        );
        if (hasUnhealthy) return 'Unhealthy';

        const hasDegraded = cloudData.pipelines.some(
            (p) =>
                p.stages &&
                p.stages[stageName] &&
                p.stages[stageName].status === 'Degraded'
        );
        if (hasDegraded) return 'Degraded';

        return 'Healthy';
    };

    const stageHealthMap = {
        Public: getStageHealth('public'),
        'Public Transfer': getStageHealth('publicTransfer'),
        'Cloud Arrive': getStageHealth('cloudArrive'),
        'Cloud Engine': getStageHealth('cloudEngine'),
        'Cloud Platform': getStageHealth('cloudPlatform'),
    };

    // Helper function to render a stage
    const renderStage = (name: string, health: HealthStatus, label: string) => {
        const stageKey = name as StageType;
        return (
            <div
                onClick={() => onStageClick(stageKey)}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor:
                        selectedStage === stageKey
                            ? getStatusColor(health) + '20'
                            : 'transparent',
                    border:
                        selectedStage === stageKey
                            ? `2px solid ${getStatusColor(health)}`
                            : '2px solid transparent',
                    transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                    if (selectedStage !== stageKey) {
                        e.currentTarget.style.backgroundColor =
                            getStatusColor(health) + '10';
                    }
                }}
                onMouseLeave={(e) => {
                    if (selectedStage !== stageKey) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }
                }}
            >
                <div
                    style={{
                        width: window.innerWidth <= 640 ? '36px' : '48px',
                        height: window.innerWidth <= 640 ? '36px' : '48px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(health),
                        border: `4px solid ${getStatusColor(health)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: theme.effects.elevation8,
                        marginBottom: '8px',
                    }}
                >
                    <span
                        style={{
                            fontSize:
                                window.innerWidth <= 640 ? '18px' : '24px',
                            color: 'white',
                            fontWeight: 'bold',
                        }}
                    >
                        {getStatusIcon(health)}
                    </span>
                </div>
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            fontWeight: 600,
                            color: theme.palette.neutralPrimary,
                            textAlign: 'center',
                        },
                    }}
                >
                    {name}
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: getStatusColor(health),
                            fontWeight: 600,
                            fontSize: '11px',
                            textAlign: 'center',
                        },
                    }}
                >
                    {health}
                </Text>
                <Text
                    variant="tiny"
                    styles={{
                        root: {
                            color: theme.palette.neutralTertiary,
                            fontSize: '9px',
                            textAlign: 'center',
                            marginTop: '2px',
                        },
                    }}
                >
                    {label}
                </Text>
            </div>
        );
    };

    // Helper function to render a connection
    const renderConnection = (
        timeLabel: string,
        throughputLabel: string,
        isDashed: boolean
    ) => {
        const isMobile = window.innerWidth <= 768;

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    marginLeft: isMobile ? '0px' : '10px',
                    marginRight: isMobile ? '0px' : '15px',
                    minWidth: isMobile ? '100%' : 'auto',
                }}
            >
                <Text
                    variant="tiny"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            fontWeight: 500,
                            fontSize: isMobile ? '11px' : '10px',
                            whiteSpace: 'nowrap',
                            order: isMobile ? 1 : 0,
                        },
                    }}
                >
                    {timeLabel}
                </Text>
                <div
                    style={{
                        width: isMobile
                            ? '100%'
                            : window.innerWidth <= 640
                            ? '60px'
                            : '90px',
                        height: isMobile ? '50px' : '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        order: isMobile ? 0 : 1,
                    }}
                >
                    <div
                        style={{
                            width: isMobile ? '8px' : '100%',
                            height: isMobile ? '100%' : '8px',
                            backgroundColor: isDashed
                                ? 'transparent'
                                : theme.palette.neutralLight,
                            background: isDashed
                                ? isMobile
                                    ? `repeating-linear-gradient(180deg, ${theme.palette.themeDark}, ${theme.palette.themeDark} 6px, transparent 6px, transparent 12px)`
                                    : `repeating-linear-gradient(90deg, ${theme.palette.themeDark}, ${theme.palette.themeDark} 6px, transparent 6px, transparent 12px)`
                                : theme.palette.neutralLight,
                            position: 'relative',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                ...(isMobile
                                    ? {
                                          bottom: '-8px',
                                          left: '-6px',
                                          width: 0,
                                          height: 0,
                                          borderTop: `9px solid ${
                                              isDashed
                                                  ? theme.palette.themeDark
                                                  : theme.palette.neutralLight
                                          }`,
                                          borderLeft: '9px solid transparent',
                                          borderRight: '9px solid transparent',
                                      }
                                    : {
                                          right: '-8px',
                                          top: '-6px',
                                          width: 0,
                                          height: 0,
                                          borderLeft: `9px solid ${
                                              isDashed
                                                  ? theme.palette.themeDark
                                                  : theme.palette.neutralLight
                                          }`,
                                          borderTop: '9px solid transparent',
                                          borderBottom: '9px solid transparent',
                                      }),
                            }}
                        />
                    </div>
                </div>
                <Text
                    variant="tiny"
                    styles={{
                        root: {
                            color: theme.palette.themePrimary,
                            fontWeight: 600,
                            fontSize: isMobile ? '11px' : '10px',
                            whiteSpace: 'nowrap',
                            order: isMobile ? 2 : 2,
                        },
                    }}
                >
                    {throughputLabel}
                </Text>
            </div>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
            }}
        >
            {/* Pipeline Diagram - Centered and Full Width */}
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {/* Pipeline Flow Visualization */}
                <div style={{ width: '100%' }}>
                    {/* Wrapper for horizontal scroll on mobile */}
                    <div
                        style={{
                            overflowX: 'auto',
                            overflowY: 'visible',
                            width: '100%',
                            paddingBottom: '16px',
                        }}
                    >
                        {/* Main Pipeline Container - Horizontal on desktop, vertical on mobile */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: isMobileWidth()
                                    ? 'column'
                                    : 'row',
                                alignItems: isMobileWidth()
                                    ? 'stretch'
                                    : 'flex-start',
                                justifyContent: 'center',
                                gap: isMobileWidth() ? '24px' : '0px',
                                minWidth: isMobileWidth() ? 'auto' : '900px',
                            }}
                        >
                            {/* Public Stages Group */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isMobileWidth()
                                        ? 'stretch'
                                        : 'center',
                                }}
                            >
                                {/* Public Stages Row */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: isMobileWidth()
                                            ? 'center'
                                            : 'flex-start',
                                        gap: '0px',
                                    }}
                                >
                                    {/* Public Stage */}
                                    {renderStage(
                                        'Public',
                                        stageHealthMap['Public'],
                                        'SRM'
                                    )}

                                    {/* Connection Arrow */}
                                    {renderConnection(
                                        '~2.3 min',
                                        '~125 Mbps',
                                        false
                                    )}

                                    {/* Public Transfer Stage */}
                                    {renderStage(
                                        'Public Transfer',
                                        stageHealthMap['Public Transfer'],
                                        'CTS'
                                    )}
                                </div>
                                {/* Public Stages Underline */}
                                <div
                                    style={{
                                        width: '100%',
                                        height: '5px',
                                        backgroundColor:
                                            theme.palette.themePrimary,
                                        marginTop: '8px',
                                        marginBottom: '12px',
                                    }}
                                />
                                {/* Public Region Info */}
                                <div
                                    style={{
                                        textAlign: 'left',
                                        width: '100%',
                                        paddingLeft: '12px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                                fontWeight: 500,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Region:{' '}
                                        {cloudData.pipelines?.[0]
                                            ?.primaryRegion || 'Public Region'}
                                    </Text>
                                    <Text
                                        variant="tiny"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralTertiary,
                                                fontSize: '11px',
                                            },
                                        }}
                                    >
                                        Source:{' '}
                                        {cloudData.pipelines?.[0]
                                            ?.sourceRegion || 'N/A'}
                                    </Text>
                                </div>
                            </div>

                            {/* Transfer Arrow */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    alignSelf: 'center',
                                    paddingTop:
                                        window.innerWidth <= 768
                                            ? '0px'
                                            : '12px',
                                    paddingBottom:
                                        window.innerWidth <= 768
                                            ? '0px'
                                            : '60px',
                                    marginLeft:
                                        window.innerWidth <= 768
                                            ? '0px'
                                            : '8px',
                                    marginRight:
                                        window.innerWidth <= 768
                                            ? '0px'
                                            : '8px',
                                }}
                            >
                                {renderConnection('~3.8 min', 'Transfer', true)}
                            </div>

                            {/* Cloud Stages Group */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems:
                                        window.innerWidth <= 768
                                            ? 'stretch'
                                            : 'center',
                                }}
                            >
                                {/* Cloud Stages Row */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent:
                                            window.innerWidth <= 768
                                                ? 'center'
                                                : 'flex-start',
                                        gap: '0px',
                                    }}
                                >
                                    {/* Cloud Arrive Stage */}
                                    {renderStage(
                                        'Cloud Arrive',
                                        stageHealthMap['Cloud Arrive'],
                                        'CTS'
                                    )}

                                    {/* Connection Arrow */}
                                    {renderConnection(
                                        '~3.1 min',
                                        '~110 Mbps',
                                        false
                                    )}

                                    {/* Cloud Engine Stage */}
                                    {renderStage(
                                        'Cloud Engine',
                                        stageHealthMap['Cloud Engine'],
                                        'SRM'
                                    )}

                                    {/* Spacer for Cloud Platform (no connector) */}
                                    <div
                                        style={{
                                            width:
                                                window.innerWidth <= 640
                                                    ? '60px'
                                                    : '90px',
                                        }}
                                    />

                                    {/* Cloud Platform Stage */}
                                    {renderStage(
                                        'Cloud Platform',
                                        stageHealthMap['Cloud Platform'],
                                        'SRM'
                                    )}
                                </div>
                                {/* Cloud Stages Underline */}
                                <div
                                    style={{
                                        width: '100%',
                                        height: '5px',
                                        backgroundColor:
                                            theme.palette.purpleDark,
                                        marginTop: '8px',
                                        marginBottom: '12px',
                                    }}
                                />
                                {/* Cloud Region Info */}
                                <div
                                    style={{
                                        textAlign: 'left',
                                        width: '100%',
                                        paddingLeft: '12px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                                fontWeight: 500,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Region:{' '}
                                        {cloudData.pipelines?.[0]
                                            ?.secondaryRegion || 'Cloud Region'}
                                    </Text>
                                    <Text
                                        variant="tiny"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralTertiary,
                                                fontSize: '11px',
                                            },
                                        }}
                                    >
                                        Destination:{' '}
                                        {cloudData.pipelines?.[0]
                                            ?.destinationRegion || 'N/A'}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
